import { Fragment, useLayoutEffect, useRef, useState } from "react";
import { sanitizeHtml } from "utils/sanitizeHtml";
import SplashHeader from "../page/global-splash/SplashHeader";
import { ImageArrowLeft, ImageX } from "components/utility/images";
import { sendAcceptContinue } from "./utility/audienceSortApiHandlers";
import { audienceSortStepNames } from "./utility/audienceSortStepNames";
import Scrollbars from "react-custom-scrollbars-2";
import Checkbox from "components/utility/checkbox/Checkbox";
import ButtonAction from "components/utility/button/ButtonAction";
import { ProfessionalAffirmationMobileProps } from "./utility/audienceSortPropTypes";

const ProfessionalAffirmationMobile = ({
	audienceSelectionData,
	currentPage,
	hasHeader = false,
	hasCloseButton = false,
	cameFromInitialStep,
	setStep,
	audienceSortTheme
}: ProfessionalAffirmationMobileProps) => {
	const audienceSortBackButton = useRef<HTMLButtonElement>(null);
	const checkBoxContainer = useRef<HTMLInputElement>(null);
	const mobileProAffirmationNavRef = useRef<HTMLDivElement>(null);

	const [activeTab, setActiveTab] = useState<number>(0);
	const [affirmationCheck, setAffirmationCheck] = useState<boolean>(false);
	const [checkboxIsError, setCheckBoxIsError] = useState<boolean>(false);

	useLayoutEffect(() => {
		if (mobileProAffirmationNavRef.current) {
			const vh: number = window.innerHeight * 0.01 * 100;
			if (currentPage === "nav") {
				mobileProAffirmationNavRef.current.setAttribute(
					"style",
					`height: calc(${vh}px - ${
						mobileProAffirmationNavRef.current.getBoundingClientRect().top
					}px); height: calc(100dvh - ${
						mobileProAffirmationNavRef.current.getBoundingClientRect().top
					}px)`
				);
			}
		}
	}, [currentPage]);

	const handleBackButton = (): void => {
		if (cameFromInitialStep) {
			setStep(audienceSortStepNames.initialStep);
		} else {
			setStep(audienceSortStepNames.audienceTypeSelection);
		}
	};

	const handleClose = (): void => {
		setStep(audienceSortStepNames.initialStep);
	};

	const toggleAffirmationCheck = (): void => {
		setAffirmationCheck(!affirmationCheck);
		setCheckBoxIsError(false);
	};

	const handleAffirmationAgreement = (): void => {
		if (affirmationCheck)
			sendAcceptContinue(audienceSelectionData, currentPage, cameFromInitialStep);
		else setCheckBoxIsError(true);
	};

	if (!audienceSelectionData) return;
	return (
		<div className={"professional-affirmation-mobile-container " + audienceSortTheme}>
			<div
				className={`professional-affirmation-mobile-header ${
					currentPage === "unknown-splash" ? "professional-affirmation-mobile-header-unknown" : ""
				}`}
			>
				{hasHeader ? (
					<div className="professional-affirmation-mobile-header-title">
						<button
							className="professional-affirmation-mobile-back-button"
							aria-label={audienceSelectionData.title}
							tabIndex={0}
							onClick={handleBackButton}
							onKeyUp={handleBackButton}
							ref={audienceSortBackButton}
						>
							<ImageArrowLeft className="caret" />
							<span>{audienceSelectionData.title}</span>
						</button>

						{hasCloseButton && (
							<button
								className="professional-affirmation-mobile-close-button"
								aria-label="Close"
								onClick={handleClose}
							>
								<ImageX />
							</button>
						)}
					</div>
				) : (
					<>
						<SplashHeader />
						<div className="professional-affirmation-splash-header">
							<div
								className="professional-affirmation-splash-title"
								dangerouslySetInnerHTML={{
									__html: sanitizeHtml(audienceSelectionData.continueAffirmationTitle)
								}}
							/>
							<div
								className="professional-affirmation-splash-notes"
								dangerouslySetInnerHTML={{
									__html: sanitizeHtml(audienceSelectionData.continueAffirmationNotes)
								}}
							/>
						</div>
					</>
				)}
				<div className="professional-affirmation-mobile-title-area">
					{audienceSelectionData.affirmations.length > 1 && (
						<div className="professional-affirmation-mobile-language">
							{audienceSelectionData.affirmations.map((affirmation, index) => {
								return (
									<Fragment key={`affirmation-${affirmation.language}`}>
										{index === 0 ? "" : <span>{` | `}</span>}
										<button
											aria-label={affirmation.language}
											tabIndex={0}
											data-a="true"
											data-a-lbl={affirmation.language}
											data-a-cat="Audience"
											data-a-act="Affirmation"
											onClick={() => setActiveTab(index)}
											onKeyUp={() => setActiveTab(index)}
											className={activeTab === index ? " active " : ""}
										>
											{affirmation.language}
										</button>
									</Fragment>
								);
							})}
						</div>
					)}

					<div className=" professional-affirmation-mobile-title">
						{audienceSelectionData.affirmations[activeTab]?.title}
					</div>
				</div>
			</div>

			{audienceSelectionData.affirmations.length > 0 && (
				<div
					className={`professional-affirmation-mobile-terms-wrapper ${
						currentPage === "unknown-splash"
							? "professional-affirmation-mobile-terms-wrapper-unknown"
							: "professional-affirmation-mobile-terms-wrapper-nav"
					}`}
					ref={mobileProAffirmationNavRef}
				>
					<div className="professional-affirmation-mobile-terms-container">
						<div className="professional-affirmation-mobile-terms-section">
							<Scrollbars
								className="terms-area"
								autoHide
								autoHideTimeout={1000}
								autoHideDuration={200}
								style={{ width: "100%", height: "100%" }}
								renderView={(props) => (
									<div
										className="professional-affirmation-mobile-terms-box"
										dangerouslySetInnerHTML={{
											__html: sanitizeHtml(
												audienceSelectionData.affirmations[activeTab].description
											)
										}}
										{...props}
										style={{
											...props.style,
											bottom: "-3px",
											right: "-3px",
											overflow: "auto",
											marginRight: "0"
										}}
									/>
								)}
							/>
						</div>
						<div className="professional-affirmation-mobile-check-area">
							<div className="checkbox-container">
								<Checkbox
									className={
										(checkboxIsError ? "check-error " : "") +
										(audienceSortTheme === "dark-theme" ? "almost-white-checkbox " : "")
									}
									id={audienceSelectionData.affirmations[activeTab].checkBoxText}
									checked={affirmationCheck}
									onChange={toggleAffirmationCheck}
									reference={checkBoxContainer}
									label={`Agree to the terms`}
									dataQa="professional-affirmation-mobile-checkbox"
								/>
								<label htmlFor={audienceSelectionData.affirmations[activeTab].checkBoxText}>
									{audienceSelectionData.affirmations[activeTab].checkBoxText}
								</label>
							</div>

							<ButtonAction
								className="professional-affirmation-mobile-button"
								type="solid"
								width="full"
								label={audienceSelectionData.affirmations[activeTab].affirmationButtonText}
								isButton="true"
								buttonType="submit"
								dataA={true}
								dataAEvt="Audience Select"
								dataACat="Audience"
								dataAAct="Affirmation"
								dataALbl={audienceSelectionData.affirmations[activeTab].affirmationButtonText}
								dataAText={audienceSelectionData.affirmations[activeTab].affirmationButtonText}
								dataAComp="audience selector modal"
								dataQa="professional-affirmation-mobile-button"
								onClick={handleAffirmationAgreement}
							/>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default ProfessionalAffirmationMobile;
